import React from 'react';
import { Helmet } from 'react-helmet-async';
import { UserCheck, Home, Heart, Coffee, ShieldCheck } from 'lucide-react';
import { siteConfig } from '../../data';
import ContactSection from '../../components/ContactSection';

export default function PersonligHjaelp() {
  return (
    <div className="bg-white">
      <Helmet>
        <title>Personlig hjælp | Hjemmebesøg og tryghed | Online Tryghed</title>
        <meta name="description" content="Vi kommer hjem til dig og hjælper med alt det digitale. Personlig service i øjenhøjde, hvor vi tager os god tid." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-[#103B66] mb-6 leading-tight">
              Personlig hjælp: Vi møder dig, hvor du er
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Nogle gange er en telefonopringning ikke nok. Vi tilbyder personligt fremmøde, så vi kan løse udfordringerne sammen i dit eget tempo og i dine vante omgivelser.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href={`tel:${siteConfig.phone.replace(/\s/g, '')}`} className="px-8 py-4 bg-[#103B66] text-white font-bold rounded-sm hover:bg-slate-800 transition-all">
                Bestil et hjemmebesøg
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#103B66] mb-6">Mere end bare IT-support</h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                <p>
                  Hos OnlineTryghed handler det ikke kun om at få teknikken til at virke. Det handler om at give dig selvtilliden tilbage til at bruge de digitale værktøjer, som er blevet en nødvendighed i dagens Danmark.
                </p>
                <p>
                  Når vi kommer på besøg, starter vi ofte med en kop kaffe og en snak om, hvad der driller. Vi tager os tid til at lytte, og vi går ikke, før du føler dig tryg ved de løsninger, vi har fundet.
                </p>
                <div className="flex items-center p-4 bg-white rounded-none border border-slate-100 shadow-md">
                  <ShieldCheck className="w-6 h-6 text-black mr-3" />
                  <span className="text-gray-900 font-medium italic">"Vi taler dansk, ikke teknik-sprog."</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-6 rounded-none border border-slate-100 shadow-sm flex flex-col justify-between">
                <div>
                  <Home className="w-8 h-8 text-black mb-4" />
                  <h3 className="font-bold text-[#103B66] text-lg">I dit hjem</h3>
                </div>
                <p className="text-sm text-slate-500 mt-2">Vi kommer til dig på hele Sjælland.</p>
              </div>
              <div className="bg-white p-6 rounded-none border border-slate-100 shadow-sm flex flex-col justify-between">
                <div>
                  <UserCheck className="w-8 h-8 text-black mb-4" />
                  <h3 className="font-bold text-[#103B66] text-lg">Fast kontakt</h3>
                </div>
                <p className="text-sm text-slate-500 mt-2">Du får den samme rådgiver hver gang.</p>
              </div>
              <div className="bg-white p-6 rounded-none border border-slate-100 shadow-sm flex flex-col justify-between">
                <div>
                  <Coffee className="w-8 h-8 text-black mb-4" />
                  <h3 className="font-bold text-[#103B66] text-lg">Roligt tempo</h3>
                </div>
                <p className="text-sm text-slate-500 mt-2">Ingen stress eller jag. Vi tager den tid, der skal til.</p>
              </div>
              <div className="bg-white p-6 rounded-none border border-slate-100 shadow-sm flex flex-col justify-between">
                <div>
                  <Heart className="w-8 h-8 text-black mb-4" />
                  <h3 className="font-bold text-[#103B66] text-lg">Omsorg</h3>
                </div>
                <p className="text-sm text-slate-500 mt-2">Vi passer på dine data og din tryghed.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Examples */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#103B66] text-center mb-12">Eksempler på personlig hjælp</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Udpakning & Installation",
                desc: "Har du købt nyt udstyr? Vi hjælper med at pakke det ud, sætte det op og overføre dine gamle filer."
              },
              {
                title: "Digital Oprydning",
                desc: "Vi hjælper med at slette gamle filer, organisere dine billeder og sikre, at din computer kører optimalt."
              },
              {
                title: "Læring & Vejledning",
                desc: "Vil du lære at bruge Facebook, MitID eller netbank? Vi sidder ved siden af dig og guider dig igennem det."
              }
            ].map((item, i) => (
              <div key={i} className="bg-white p-8 rounded-none shadow-sm border border-slate-100">
                <h3 className="text-xl font-bold text-[#103B66] mb-4">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Summary Section */}
      <section className="py-16 bg-white border-t border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#103B66] mb-4">Gennemskuelige priser på personlig hjælp</h2>
          <p className="text-slate-600 mb-10">
            Vi fakturerer pr. påbegyndt halve time, så du kun betaler for den tid, vi rent faktisk bruger.
          </p>
          <div className="grid sm:grid-cols-2 gap-6 mb-8 text-left">
            <div className="bg-slate-50 p-6 border border-slate-100">
              <h3 className="font-bold text-[#103B66] text-lg mb-2">Personligt fremmøde</h3>
              <p className="text-2xl font-extrabold text-slate-900 mb-2">349,- <span className="text-sm font-normal text-slate-500">/ pr. påbegyndt 1/2 time</span></p>
              <p className="text-sm text-slate-500">Vi kommer hjem til dig i trygge rammer.</p>
            </div>
            <div className="bg-slate-50 p-6 border border-slate-100">
              <h3 className="font-bold text-[#103B66] text-lg mb-2">Kørsel & transport</h3>
              <p className="text-2xl font-extrabold text-slate-900 mb-2">10,- <span className="text-sm font-normal text-slate-500">/ pr. km til kunden</span></p>
              <p className="text-sm text-slate-500">Udkørsel fra Slagelse. Hjemturen faktureres ikke.</p>
            </div>
          </div>
          <p className="text-xs text-slate-400">Alle priser er inkl. moms. Vi fakturerer pr. påbegyndt halve time.</p>
        </div>
      </section>

      <ContactSection />
    </div>
  );
}
